import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { AlertCircle, Check, FileText, Loader2, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, useToast } from '@/components/ui';
import { AI_BUDGET_ERROR, isRunning, learningApi, learningKeys } from '../learning/learning-api';
import { setupApi } from '../onboarding/setup-api';
import { GreenInvoiceCard } from '../settings/green-invoice-card';

/* ---------------------------------------------------------------------------
   כרטיסים שהעוזר מציג בתוך השיחה (message.links): מצב הלמידה ממסמכים,
   וחיבור Google / חשבונית ירוקה. כך ההקמה כולה נשארת בצ׳ט — בלי לעבור מסך.
   --------------------------------------------------------------------------- */

const shell = 'mt-2 rounded-(--radius-lg) border border-border bg-surface-raised p-3 text-sm';

/** `/learn?run=<id>` → id. */
export function runIdOf(href: string): string | null {
  const id = new URLSearchParams(href.split('?')[1] ?? '').get('run');
  return id || null;
}

const KINDS = ['PRICE_ITEM', 'CUSTOMER', 'JOB_TYPE', 'SUPPLIER', 'MATERIAL'] as const;

export function LearningRunCard({ runId, href }: { runId: string; href: string }) {
  const { t } = useTranslation('assistant');
  const qc = useQueryClient();
  const toast = useToast();
  const router = useRouter();

  const run = useQuery({
    queryKey: learningKeys.run(runId),
    queryFn: ({ signal }) => learningApi.run(runId, signal),
    refetchInterval: (q) => (isRunning(q.state.data) ? 2_000 : false),
  });

  const acceptAll = useMutation({
    mutationFn: async () => {
      const res = await learningApi.accept(runId);
      await learningApi.complete(runId);
      return res;
    },
    onSuccess: async (res) => {
      toast.success(t('cards.learn.added', { count: res.accepted }));
      await qc.invalidateQueries({ queryKey: learningKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const data = run.data;
  if (!data) {
    return (
      <div className={shell} role="status">
        <Loader2 className="inline size-3.5 animate-spin" aria-hidden /> {t('cards.learn.loading')}
      </div>
    );
  }

  if (isRunning(data)) {
    return (
      <div className={shell} role="status">
        <p className="flex items-center gap-2 font-medium text-fg">
          <Loader2 className="size-3.5 animate-spin text-accent" aria-hidden />
          {t('cards.learn.reading')}
        </p>
        <p className="mt-1 text-xs text-fg-muted">
          {t('cards.learn.progress', { done: data.docsParsed, total: data.filesTotal })}
        </p>
      </div>
    );
  }

  if (data.status === 'FAILED') {
    return (
      <div className={shell} role="alert">
        <p className="flex items-center gap-2 font-medium text-danger">
          <AlertCircle className="size-3.5" aria-hidden />
          {data.error === AI_BUDGET_ERROR ? t('cards.learn.budget') : t('cards.learn.failed')}
        </p>
      </div>
    );
  }

  const pending = (kind: (typeof KINDS)[number]) =>
    data.suggestions
      .filter((s) => s.kind === kind && s.status === 'PENDING')
      .reduce((n, s) => n + s.count, 0);
  const found = KINDS.map((k) => ({ kind: k, count: pending(k) })).filter((x) => x.count > 0);
  const done = data.status === 'COMPLETED';

  return (
    <div className={shell}>
      <p className="flex items-center gap-2 font-medium text-fg">
        <FileText className="size-3.5 text-accent" aria-hidden />
        {done
          ? t('cards.learn.doneTitle')
          : t('cards.learn.foundTitle', { count: data.docsParsed })}
      </p>
      {!done && found.length > 0 ? (
        <ul className="mt-1.5 space-y-0.5 text-xs text-fg-muted">
          {found.map((x) => (
            <li key={x.kind}>{t(`cards.learn.kinds.${x.kind}`, { count: x.count })}</li>
          ))}
        </ul>
      ) : null}
      {!done && found.length === 0 ? (
        <p className="mt-1 text-xs text-fg-muted">{t('cards.learn.nothing')}</p>
      ) : null}
      {done ? null : (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {found.length > 0 ? (
            <Button size="sm" onClick={() => acceptAll.mutate()} loading={acceptAll.isPending}>
              {t('cards.learn.addAll')}
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" onClick={() => router.history.push(href)}>
            {t('cards.learn.review')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function ConnectGoogleCard() {
  const { t } = useTranslation('assistant');
  const toast = useToast();
  const status = useQuery({
    queryKey: ['onboarding-import', 'status'],
    queryFn: ({ signal }) => setupApi.status(signal),
  });
  const connect = useMutation({
    mutationFn: setupApi.connectGoogle,
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
    onError: (err: Error) => toast.error(err.message),
  });
  const connected =
    status.data?.google.gmail === 'CONNECTED' || status.data?.google.drive === 'CONNECTED';

  return (
    <div className={shell}>
      <p className="flex items-center gap-2 font-medium text-fg">
        <Mail className="size-3.5 text-accent" aria-hidden />
        {t('cards.google.title')}
      </p>
      <p className="mt-1 text-xs text-fg-muted">{t('cards.google.body')}</p>
      <div className="mt-2.5">
        {connected ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
            <Check className="size-3.5" aria-hidden />
            {t('cards.connected')}
          </span>
        ) : (
          <Button size="sm" onClick={() => connect.mutate()} loading={connect.isPending}>
            {t('cards.google.connect')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function ConnectGreenInvoiceCard() {
  const { t } = useTranslation('assistant');
  return (
    <div className="mt-2">
      <GreenInvoiceCard description={t('cards.greenInvoice.body')} />
    </div>
  );
}
